import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
  Pagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import YouTubeIcon from '@mui/icons-material/YouTube';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';

const Videos = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    videoId: null,
    videoTitle: ''
  });

  // Fetch videos
  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      let url = `/api/videos?page=${page}&limit=10`;
      
      if (searchTerm) {
        url += `&search=${searchTerm}`;
      }
      
      if (categoryFilter) {
        url += `&category=${categoryFilter}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setVideos(response.data.data);
      setTotalPages(response.data.pages);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setError('Ошибка при загрузке видео');
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/videos/categories', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Initial load
  useEffect(() => {
    fetchVideos();
    fetchCategories();
  }, [page]);

  // Handle search
  const handleSearch = () => {
    setPage(1);
    fetchVideos();
  };

  // Handle category filter change
  const handleCategoryChange = (event) => {
    setCategoryFilter(event.target.value);
    setPage(1);
    setTimeout(() => {
      fetchVideos();
    }, 100);
  };

  // Handle delete dialog
  const openDeleteDialog = (id, title) => {
    setDeleteDialog({
      open: true,
      videoId: id,
      videoTitle: title
    });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      videoId: null,
      videoTitle: ''
    });
  };

  // Delete video
  const handleDelete = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      await axios.delete(`/api/videos/${deleteDialog.videoId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Refresh videos
      fetchVideos();
      closeDeleteDialog();
    } catch (error) {
      console.error('Error deleting video:', error);
      setError('Ошибка при удалении видео');
      setLoading(false);
    }
  };

  // Toggle video visibility
  const toggleVideoVisibility = async (id, isActive) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/videos/${id}`, 
        { isActive: !isActive },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Update local state
      setVideos(videos.map(video => 
        video._id === id ? { ...video, isActive: !isActive } : video
      ));
    } catch (error) {
      console.error('Error updating video visibility:', error);
      setError('Ошибка при обновлении видимости видео');
    }
  };

  // Get video source icon
  const getSourceIcon = (source) => {
    switch (source) {
      case 'youtube':
        return <YouTubeIcon color="error" />;
      case 'vimeo':
        return <OndemandVideoIcon color="primary" />;
      default:
        return <OndemandVideoIcon />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Управление видео
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/videos/new')}
        >
          Добавить видео
        </Button>
      </Box>
      
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="Поиск видео"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ flexGrow: 1, minWidth: '200px' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: '200px' }}>
            <InputLabel id="category-filter-label">Категория</InputLabel>
            <Select
              labelId="category-filter-label"
              id="category-filter"
              value={categoryFilter}
              label="Категория"
              onChange={handleCategoryChange}
            >
              <MenuItem value="">Все категории</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>
      
      {/* Videos table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Источник</TableCell>
                  <TableCell>Категория</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {videos.length > 0 ? (
                  videos.map((video) => (
                    <TableRow key={video.id || `video-${video.title}-${Math.random()}`}>
                      <TableCell>
                        <Typography variant="subtitle2">{video.title}</Typography>
                        {video.description && (
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 250 }}>
                            {video.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getSourceIcon(video.source)}
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {video.source}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {video.category ? (
                          <Chip label={video.category} size="small" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Без категории
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={video.isActive ? "Активно" : "Скрыто"} 
                          color={video.isActive ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          color={video.isActive ? "default" : "primary"}
                          onClick={() => toggleVideoVisibility(video.id, video.isActive)}
                          title={video.isActive ? "Скрыть видео" : "Показать видео"}
                        >
                          {video.isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                        <IconButton 
                          color="primary"
                          onClick={() => navigate(`/admin/videos/edit/${video.id}`)}
                          title="Редактировать"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          color="error"
                          onClick={() => openDeleteDialog(video.id, video.title)}
                          title="Удалить"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body1" sx={{ py: 2 }}>
                        Видео не найдены
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={(e, value) => setPage(value)} 
                color="primary" 
              />
            </Box>
          )}
        </>
      )}
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы действительно хотите удалить видео "{deleteDialog.videoTitle}"? Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Отмена
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Videos;
